<template>
  <SoAdminPage title="Features Admin">
    <template #subtitle>
      Feature flags live in the be-identity service feature table. Toggle any
      flags you want to change, then click <strong>Review &amp; Save</strong> to
      persist. Changes take effect immediately.
    </template>
    <SoAlert
      v-if="featuresList.error"
      title="Failed to render features table"
      data-testid="features-error-alert"
    />
    <SoDataTable
      v-else
      :headers="featuresTableHeaders"
      :items="items"
      :loading="featuresList.isFetching"
      :sort-by="[{ key: 'name', order: 'asc' }]"
      data-testid="features-data-table"
    >
      <template #tableActions>
        <SoButton
          v-if="!hasUnSavedChanges"
          title="Recent changes"
          large
          data-testid="features-recent-changes-btn"
          @click="openRecentChanges()"
        />
        <div v-if="hasUnSavedChanges">
          <SoButton
            title="Cancel"
            class="mr-2"
            large
            data-testid="features-cancel-btn"
            @click="resetChanges()"
          />
          <SoButton
            title="Review & Save"
            type="success"
            large
            data-testid="features-save-btn"
            @click="saveChanges()"
          />
        </div>
      </template>
      <template #item.description="rowData">
        <div
          class="description-cell"
          :title="rowData.internalItem.raw.description || ''"
        >
          {{ rowData.internalItem.raw.description || "—" }}
        </div>
      </template>
      <template #item.history="rowData">
        <SoButton
          title="History"
          small
          data-testid="features-history-btn"
          @click="openHistory(rowData.internalItem.raw as Feature)"
        />
      </template>
      <template #item.enabled="rowData">
        <SoToggle
          v-if="findFeatureCopy(rowData.internalItem.raw.id)"
          v-model="
            (findFeatureCopy(rowData.internalItem.raw.id) as Feature).enabled
          "
          name="feature-toggle"
          data-testid="features-toggle"
        />
      </template>
    </SoDataTable>
  </SoAdminPage>
  <AdminFeaturesModal
    v-model="showAreYouSureModal"
    :features="unSavedChanges"
    @success="refetch()"
  />
  <FeatureChangesDialog
    v-model="showChanges"
    :feature-id="changesFeature?.id ?? null"
    :feature-name="changesFeature?.name ?? ''"
    :current-features="featuresList.data"
  />
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import { FeaturesService } from "@/services/admin/FeaturesService"
  import {
    SoDataTableColumnType,
    SoDataTableHeader,
    SoDataTableItem,
  } from "@/types/components/SoDataTable"
  import { cloneDeep } from "lodash-es"
  import { Feature } from "@/models/admin/feature/feature"
  import SoAdminPage from "@/components/templates/SoAdminPage.vue"
  import SoDataTable from "@/components/dataTable/SoDataTable.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SoToggle from "@/components/formControls/SoToggle.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import AdminFeaturesModal from "./modals/AdminFeaturesModal.vue"
  import FeatureChangesDialog from "./components/FeatureChangesDialog.vue"

  const headers: SoDataTableHeader[] = [
    { title: "Feature", key: "name" },
    { title: "Team", key: "squadName" },
    { title: "Description", key: "description", sortable: false },
    {
      title: "Created",
      key: "createdAt",
      type: SoDataTableColumnType.DATE_TIME,
      fallbackValue: "—",
    },
    {
      title: "Last changed",
      key: "updatedAt",
      type: SoDataTableColumnType.DATE_TIME,
      fallbackValue: "—",
    },
    { title: "", key: "history", sortable: false },
    { title: "Enabled", key: "enabled", sortable: false },
  ]

  export default defineComponent({
    components: {
      SoAdminPage,
      SoDataTable,
      SoAlert,
      SoToggle,
      SoButton,
      AdminFeaturesModal,
      FeatureChangesDialog,
    },
    data() {
      return {
        featuresTableHeaders: headers,
        featuresList: FeaturesService.getFeaturesAdminList(),
        featuresQueryCopy: [] as Feature[],
        unSavedChanges: [] as Feature[],
        showAreYouSureModal: false,
        showChanges: false,
        changesFeature: null as Feature | null,
      }
    },
    computed: {
      items(): SoDataTableItem[] {
        return this.featuresList.data as unknown as SoDataTableItem[]
      },
      hasUnSavedChanges() {
        return (
          JSON.stringify(this.featuresList.data) !==
          JSON.stringify(this.featuresQueryCopy)
        )
      },
    },
    watch: {
      "featuresList.data"(value) {
        this.featuresQueryCopy = cloneDeep(value)
      },
    },
    mounted() {
      this.featuresQueryCopy = cloneDeep(this.featuresList.data)
    },
    methods: {
      findFeatureCopy(id: number) {
        return this.featuresQueryCopy.find(
          (feature: Feature) => feature.id === id,
        )
      },
      saveChanges() {
        this.unSavedChanges = this.featuresQueryCopy.filter(
          (ftCpy) =>
            ftCpy.enabled !==
            this.featuresList.data.find((ft) => ft.id === ftCpy.id)?.enabled,
        )
        this.showAreYouSureModal = true
      },
      refetch() {
        this.featuresList.refetch()
      },
      openHistory(feature: Feature) {
        this.changesFeature = feature
        this.showChanges = true
      },
      openRecentChanges() {
        this.changesFeature = null
        this.showChanges = true
      },
      resetChanges() {
        this.featuresQueryCopy = cloneDeep(this.featuresList.data)
      },
    },
  })
</script>

<style scoped>
  :deep(td:has(.description-cell)) {
    width: 100%;
  }

  .description-cell {
    line-height: 1;
    white-space: normal;
    word-break: break-word;
    padding-block: 0.25rem;
  }
</style>
